import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  localStorageService,
  sessionStorageService,
} from "@utils/storageService/StorageService";
import { useIdleTimer } from "react-idle-timer";
import useAuth from "@features/auth/useAuth";
import { useOnVisibilityAndInterval } from "@hooks/useOnVisibilityAndInterval";

const CHECK_AND_REAUTH_INTERVAL = 30 * 1000; // 30 seconds
const INACTIVITY_LOGOUT_INTERVAL = 60 * 30 * 1000; // 30 minutes

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const getPathToRedirectToOnLogout = () => {
    const practiceId = localStorageService.get("fastr_practiceId");
    const fastrId = localStorageService.get("fastr_fastrId");
    const fastrHash = localStorageService.get("fastr_fastrHash");

    if (practiceId && fastrId && fastrHash) {
      return `/${practiceId}/${fastrId}/${fastrHash}`;
    }

    return "/";
  };

  const navigate = useNavigate();

  const {
    checkAndReauth,
    logout: [logout],
  } = useAuth();

  useIdleTimer({
    timeout: INACTIVITY_LOGOUT_INTERVAL,
    onIdle: async () => {
      try {
        logout();
      } catch (error) {
        console.error("Error in logout:", error);
      } finally {
        navigate(getPathToRedirectToOnLogout(), { replace: true });
      }
    },
    throttle: 1000,
  });

  // An auth check will occur every 30 seconds to determine if either
  // redirecting to the pin page or re-authenticate is necessary.
  // To handle the tab going inactive, this also registers listeners
  // to run the check when the tab is focused or becomes visible.
  const checkAndHandleAuth = useCallback(() => {
    const token = sessionStorageService.get("fastr_token");
    if (!token) {
      navigate(getPathToRedirectToOnLogout(), { replace: true });
      return;
    }
    checkAndReauth();
  }, [checkAndReauth, navigate]);

  useOnVisibilityAndInterval({
    callback: checkAndHandleAuth,
    intervalMs: CHECK_AND_REAUTH_INTERVAL,
    runOnEffectFire: true,
  });

  return <>{children}</>;
};
